// src/Payroll_Management/PayRoll_Report/PerformanceRemarksReport.jsx
import { useEffect, useContext, useMemo, useRef, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Building,
  Users,
  Hash,
  Briefcase,
  Layers,
  BadgeCheck,
  Filter,
  ExternalLink,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}
function toNum(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

// 5-page window pagination
const MAX_PAGE_BUTTONS = 5;
const getPageWindow = (current, totalPages) => {
  if (totalPages <= MAX_PAGE_BUTTONS) return { start: 1, end: totalPages };
  const half = Math.floor(MAX_PAGE_BUTTONS / 2); // 2
  let start = current - half;
  let end = current + half;
  if (start < 1) {
    start = 1;
    end = MAX_PAGE_BUTTONS;
  }
  if (end > totalPages) {
    end = totalPages;
    start = totalPages - MAX_PAGE_BUTTONS + 1;
  }
  return { start, end };
};

// small debounce helper
function useDebouncedValue(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const PerformanceRemarksReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // auth/session values
  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("BranchId") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("BranchType") ||
    "HO";

  // meta
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [grades, setGrades] = useState([]);

  // filters
  const [empname, setEmpname] = useState("");
  const [empcode, setEmpcode] = useState("");
  const [designation, setDesignation] = useState("Select");
  const [branch, setBranch] = useState("Select");
  const [department, setDepartment] = useState("Select");
  const [grade, setGrade] = useState("Select");
  const [pfnon, setPfnon] = useState("Select"); // Select | Yes | No

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-46", []);

  // autocomplete
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);

  const debEmpcode = useDebouncedValue(empcode, 300);
  const debEmpname = useDebouncedValue(empname, 300);

  const codeBoxRef = useRef(null);
  const nameBoxRef = useRef(null);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${
    isDark ? "text-gray-200" : "text-gray-700"
  }`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const loadMeta = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId) || !s(branchType)) {
      toast.warn("Missing login Branch / Branch Type in localStorage.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(
        `${BASE_URL}/api/performance-remarks-report/meta`,
        {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(loginBranchId),
            branchType,
          },
          timeout: 60000,
        }
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setBranches(res.data.branches || []);
      setDesignations(res.data.designations || []);
      setDepartments(res.data.departments || []);
      setGrades(res.data.grades || []);

      // ASP default branch selected = session branchid
      setBranch(String(res.data.defaultBranchId || "Select"));
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta error");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId) || !s(branchType)) {
      toast.warn("Missing login Branch / Branch Type in localStorage.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(
        `${BASE_URL}/api/performance-remarks-report/data`,
        {
          params: {
            finYearId: Number(finYearId),
            branchType,
            loginBranchId: Number(loginBranchId),

            empcode: empcode.trim(),
            empname: empname.trim(),
            designation: designation === "Select" ? "" : designation,
            branch: branch === "Select" ? "" : branch,
            department: department === "Select" ? "" : department,
            grade: grade === "Select" ? "" : grade,
            pfnon: pfnon === "Select" ? "" : pfnon, // Yes | No

            page: p,
            pageSize,
          },
          timeout: 60000,
        }
      );

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Data load error");
    } finally {
      setLoading(false);
    }
  };

  // init
  useEffect(() => {
    (async () => {
      await loadMeta();
      await fetchList(1);
      setPage(1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // page change
  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // autocomplete: code
  useEffect(() => {
    const run = async () => {
      try {
        const prefix = s(debEmpcode);
        if (prefix.length < 1) {
          setCodeSug([]);
          return;
        }
        const res = await axios.get(
          `${BASE_URL}/api/performance-remarks-report/autocomplete-code`,
          {
            params: {
              finYearId: Number(finYearId),
              branchId: Number(loginBranchId),
              branchType,
              prefix,
            },
            timeout: 60000,
          }
        );
        if (res?.data?.success) setCodeSug(res.data.items || []);
      } catch {
        // silent
      }
    };
    if (s(finYearId) && s(loginBranchId) && s(branchType)) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debEmpcode]);

  // autocomplete: name
  useEffect(() => {
    const run = async () => {
      try {
        const prefix = s(debEmpname);
        if (prefix.length < 1) {
          setNameSug([]);
          return;
        }
        const res = await axios.get(
          `${BASE_URL}/api/performance-remarks-report/autocomplete-name`,
          {
            params: {
              finYearId: Number(finYearId),
              branchId: Number(loginBranchId),
              branchType,
              prefix,
            },
            timeout: 60000,
          }
        );
        if (res?.data?.success) setNameSug(res.data.items || []);
      } catch {
        // silent
      }
    };
    if (s(finYearId) && s(loginBranchId) && s(branchType)) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debEmpname]);

  // hide suggestions when clicking outside
  useEffect(() => {
    const onDoc = (e) => {
      if (codeBoxRef.current && !codeBoxRef.current.contains(e.target)) {
        setShowCodeSug(false);
      }
      if (nameBoxRef.current && !nameBoxRef.current.contains(e.target)) {
        setShowNameSug(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleViewAll = async () => {
    setEmpcode("");
    setEmpname("");
    setBranch("Select");
    setDesignation("Select");
    setGrade("Select");
    setPfnon("Select");
    setDepartment("Select");

    setPage(1);
    await fetchList(1);
  };

  const openRemarks = (employeeId) => {
    if (!employeeId) return;
    window.open(`/PayRoll/RemarksView?employeeId=${employeeId}`, "_blank");
  };

  const openPerformance = (employeeId) => {
    if (!employeeId) return;
    window.open(`/PayRoll/PerformanceView?employeeId=${employeeId}`, "_blank");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const { start: pageStart, end: pageEnd } = getPageWindow(page, totalPages);
  const pageNumbers = [];
  for (let p = pageStart; p <= pageEnd; p++) pageNumbers.push(p);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PERFORMANCE & REMARKS REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Performance & Remarks
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filters / Pagination / View Employee Remarks & Performance
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleViewAll} className={ghostBtn} disabled={loading}>
                <Filter className="w-4 h-4" />
                View All
              </button>
            </div>
          </div>

          {/* Filter card */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Employee Name */}
              <div ref={nameBoxRef} className="relative">
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 flex items-center gap-2">
                  <Users className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empname}
                    onChange={(e) => setEmpname(e.target.value)}
                    onFocus={() => setShowNameSug(true)}
                    placeholder="Type employee name..."
                    className={inputBase}
                  />
                </div>

                {/* suggestions */}
                {showNameSug && nameSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {nameSug.slice(0, 10).map((it, idx) => (
                      <button
                        type="button"
                        key={idx}
                        onClick={() => {
                          setEmpname(it);
                          setShowNameSug(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${
                          isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"
                        }`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Employee Code */}
              <div ref={codeBoxRef} className="relative">
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 flex items-center gap-2">
                  <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empcode}
                    onChange={(e) => setEmpcode(e.target.value)}
                    onFocus={() => setShowCodeSug(true)}
                    placeholder="Type employee code..."
                    className={inputBase}
                  />
                </div>

                {/* suggestions */}
                {showCodeSug && codeSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {codeSug.slice(0, 10).map((it, idx) => (
                      <button
                        type="button"
                        key={idx}
                        onClick={() => {
                          setEmpcode(it);
                          setShowCodeSug(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${
                          isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"
                        }`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Designation */}
              <div>
                <label className={labelBase}>Designation</label>
                <div className="mt-2 flex items-center gap-2">
                  <Briefcase className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={inputBase}
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    disabled={loading}
                  >
                    <option value="Select">Select</option>
                    {designations.map((d) => (
                      <option key={d.fld_desigid} value={String(d.fld_desigid)}>
                        {d.fld_designationname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <div className="mt-2 flex items-center gap-2">
                  <Building className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={inputBase}
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                    disabled={loading}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={b.fld_Branchid} value={String(b.fld_Branchid)}>
                        {b.fld_BranchName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Department */}
              <div>
                <label className={labelBase}>Department</label>
                <div className="mt-2 flex items-center gap-2">
                  <Layers className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={inputBase}
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    disabled={loading}
                  >
                    <option value="Select">Select</option>
                    {departments.map((d) => (
                      <option key={d.fld_deptid} value={String(d.fld_deptid)}>
                        {d.fld_departmentname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Grade */}
              <div>
                <label className={labelBase}>Grade</label>
                <div className="mt-2 flex items-center gap-2">
                  <BadgeCheck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={inputBase}
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    disabled={loading}
                  >
                    <option value="Select">Select</option>
                    {grades.map((g) => (
                      <option key={g.fld_gradeid} value={String(g.fld_gradeid)}>
                        {g.fld_gradename}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* PF/Non PF */}
              <div>
                <label className={labelBase}>PF / Non PF</label>
                <div className="mt-2 flex items-center gap-2">
                  <Filter className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={inputBase}
                    value={pfnon}
                    onChange={(e) => setPfnon(e.target.value)}
                    disabled={loading}
                  >
                    <option value="Select">Select</option>
                    <option value="Yes">PF</option>
                    <option value="No">Non PF</option>
                  </select>
                </div>
              </div>

              {/* Search button */}
              <div className="flex md:justify-end items-end">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Remarks</th>
                    <th className="px-4 py-4 text-center font-bold">Performance</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 6 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const empId = r.fld_Employeeid ?? r.fld_employeeid ?? r.EmployeeID ?? r.employeeid;
                      return (
                        <tr
                          key={`${empId}-${idx}`}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employeecode ?? r.fld_employeecode ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employee ?? r.fld_employee ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_designationname ?? ""}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => openRemarks(empId)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-bold rounded-2xl hover:bg-emerald-700 transform hover:-translate-y-0.5 transition-all shadow"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Remarks
                            </button>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => openPerformance(empId)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-2xl hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all shadow"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Performance
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {pageNumbers.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === p
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default PerformanceRemarksReport;
